<template>
  <div class="card">
    <div class="flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
      <div>
        <h3 class="m-0">Metadata de enlaces (defaults)</h3>
        <small class="text-500">
          Piso para todas las congregaciones. Cada una puede pisarlo desde su propio panel de
          metadata de enlaces.
        </small>
      </div>
      <div class="flex gap-2">
        <Button
          icon="pi pi-upload"
          label="Subir imagen"
          size="small"
          severity="secondary"
          outlined
          :loading="uploading"
          @click="fileInput?.click()"
        />
        <Button icon="pi pi-check" label="Guardar" size="small" :loading="saving" @click="save" />
      </div>
    </div>

    <input
      ref="fileInput"
      type="file"
      accept="image/jpeg,image/png,image/webp,image/gif"
      class="link-file-input"
      @change="onFilePicked"
    />

    <div v-if="loading" class="text-500 text-sm">Cargando…</div>

    <template v-else>
      <div class="flex flex-column gap-1 mb-3">
        <label class="text-sm font-medium">Tipo de enlace</label>
        <Dropdown
          v-model="selectedType"
          :options="linkTypes"
          optionLabel="label"
          optionValue="type"
          class="w-full md:w-25rem"
        />
        <small class="text-500">{{ currentTypeConfig?.description }}</small>
      </div>

      <div class="link-grid">
        <div class="flex flex-column gap-3">
          <div class="flex flex-column gap-1">
            <label class="text-sm font-medium">Título</label>
            <InputText v-model="draft.title" :placeholder="`Por defecto: ${currentTypeConfig?.defaults.title}`" />
          </div>
          <div class="flex flex-column gap-1">
            <label class="text-sm font-medium">Descripción</label>
            <Textarea
              v-model="draft.description"
              rows="3"
              autoResize
              :placeholder="`Por defecto: ${currentTypeConfig?.defaults.description}`"
            />
          </div>
          <div class="flex flex-column gap-1">
            <label class="text-sm font-medium">Nombre del sitio</label>
            <InputText v-model="draft.siteName" placeholder="Por defecto: nombre de la congregación" />
          </div>
          <div>
            <Button
              v-if="hasSavedEntry"
              label="Restablecer"
              icon="pi pi-undo"
              size="small"
              severity="secondary"
              text
              :loading="resetting"
              @click="confirmReset"
            />
          </div>
        </div>

        <div>
          <div class="text-sm font-medium mb-2">Vista previa</div>
          <div class="preview-card">
            <div v-if="previewImageUrl" class="preview-image">
              <img :src="previewImageUrl" alt="Imagen del preview" />
            </div>
            <div class="preview-body">
              <div class="preview-site">{{ draft.siteName || 'Territorios' }}</div>
              <div class="preview-title">{{ draft.title || currentTypeConfig?.defaults.title }}</div>
              <div class="preview-description">
                {{ draft.description || currentTypeConfig?.defaults.description }}
              </div>
            </div>
          </div>
        </div>

        <div class="link-images">
          <div class="text-sm font-medium mb-2">
            Imágenes del sitio
            <small class="text-500 font-normal">· recomendado 1200×630, hasta 5MB</small>
          </div>

          <div v-if="!images.length" class="text-500 text-sm">
            Sin imágenes propias: se usa la imagen por defecto de la app.
          </div>

          <div v-else class="image-grid">
            <div
              v-for="img in images"
              :key="img.id"
              class="image-card"
              :class="{ selected: draft.imageId === img.id }"
            >
              <img :src="imageUrl(img.url)" :alt="img.originalName" />
              <div class="image-info">
                <div class="image-name" :title="img.originalName">{{ img.originalName }}</div>
                <div class="text-xs text-500">
                  {{ img.width && img.height ? `${img.width}×${img.height} · ` : '' }}{{ formatSize(img.size) }}
                </div>
              </div>
              <div class="image-actions">
                <Button
                  v-if="draft.imageId === img.id"
                  label="Quitar"
                  icon="pi pi-times"
                  size="small"
                  severity="secondary"
                  text
                  @click="draft.imageId = null"
                />
                <Button v-else label="Usar" icon="pi pi-check" size="small" text @click="draft.imageId = img.id" />
                <Button
                  icon="pi pi-trash"
                  size="small"
                  severity="danger"
                  text
                  v-tooltip="'Eliminar imagen'"
                  @click="confirmDeleteImage(img)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <ConfirmDialog />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useConfirm } from 'primevue/useconfirm'
import { useAppToast } from '@/composables/useAppToast'
import { useAdminLinkMetadata } from '@/composables/useAdminLinkMetadata'
import { metadataImageUrl } from '@/composables/useLinkMetadata'
import Button from 'primevue/button'
import Dropdown from 'primevue/dropdown'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import ConfirmDialog from 'primevue/confirmdialog'

const confirm = useConfirm()
const toast = useAppToast()
const api = useAdminLinkMetadata()

const loading = ref(true)
const saving = ref(false)
const resetting = ref(false)
const uploading = ref(false)
const fileInput = ref(null)

const linkTypes = ref([])
const entries = ref({})
const images = ref([])
const selectedType = ref(null)

const draft = reactive({ title: '', description: '', siteName: '', imageId: null })

const currentTypeConfig = computed(() => linkTypes.value.find((t) => t.type === selectedType.value))
const hasSavedEntry = computed(() => Boolean(entries.value[selectedType.value]))

const imageUrl = (url) => metadataImageUrl(url)

const previewImage = computed(() => images.value.find((i) => i.id === draft.imageId) || null)

// Sin imagen propia, el enlace se comparte sin og:image (ver publicMeta.js).
const previewImageUrl = computed(() => (previewImage.value ? imageUrl(previewImage.value.url) : null))

const formatSize = (bytes) =>
  bytes > 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`

const applyEntryToDraft = () => {
  const entry = entries.value[selectedType.value]
  draft.title = entry?.title || ''
  draft.description = entry?.description || ''
  draft.siteName = entry?.siteName || ''
  draft.imageId = entry?.imageId || null
}

const load = async () => {
  const res = await api.getLinkMetadata()
  if (res?.error) {
    toast.error('Ocurrió un error', res.error)
    return
  }
  linkTypes.value = res.linkTypes || []
  entries.value = res.entries || {}
  images.value = res.images || []
  if (!selectedType.value) selectedType.value = linkTypes.value[0]?.type || null
  applyEntryToDraft()
}

onMounted(async () => {
  await load()
  loading.value = false
})

watch(selectedType, applyEntryToDraft)

const save = async () => {
  if (!selectedType.value) return
  saving.value = true
  try {
    const res = await api.saveLinkMetadata({ type: selectedType.value, ...draft })
    if (res?.error) {
      toast.error('Ocurrió un error', res.error)
      return
    }
    await load()
    toast.success('Metadata guardada', 'Las congregaciones que no la pisaron ya la ven.')
  } finally {
    saving.value = false
  }
}

const confirmReset = () => {
  confirm.require({
    message: '¿Volver al texto por defecto de este tipo de enlace?',
    header: 'Restablecer metadata',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    acceptLabel: 'Restablecer',
    rejectLabel: 'Cancelar',
    accept: async () => {
      resetting.value = true
      try {
        const res = await api.resetLinkMetadata(selectedType.value)
        if (res?.error) {
          toast.error('Ocurrió un error', res.error)
          return
        }
        await load()
      } finally {
        resetting.value = false
      }
    },
  })
}

const onFilePicked = async (event) => {
  const file = event.target.files?.[0]
  event.target.value = ''
  if (!file) return
  uploading.value = true
  try {
    const res = await api.uploadImage(file)
    if (res?.error) {
      toast.error('No se pudo subir', res.error)
      return
    }
    images.value = [res.image, ...images.value]
    draft.imageId = res.image.id
    toast.success('Imagen subida', 'Guardá para que la use este tipo de enlace.')
  } finally {
    uploading.value = false
  }
}

const confirmDeleteImage = (img) => {
  confirm.require({
    message: '¿Eliminar esta imagen? Si está en uso, esos enlaces vuelven a la imagen por defecto.',
    header: 'Eliminar imagen',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    acceptLabel: 'Eliminar',
    rejectLabel: 'Cancelar',
    accept: async () => {
      const res = await api.deleteImage(img.id)
      if (res?.error) {
        toast.error('Ocurrió un error', res.error)
        return
      }
      if (draft.imageId === img.id) draft.imageId = null
      await load()
    },
  })
}
</script>

<style scoped>
.link-file-input {
  display: none;
}
.link-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 320px);
  gap: var(--space-4, 1rem);
  align-items: start;
  text-align: left;
}
.link-images {
  grid-column: 1 / -1;
}

.preview-card {
  border: 1px solid var(--surface-border);
  border-radius: var(--radius-md, 8px);
  overflow: hidden;
}
.preview-image {
  aspect-ratio: 1200 / 630;
  background: var(--surface-100);
}
.preview-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.preview-body {
  padding: 0.75rem;
}
.preview-site {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--label-secondary, #6b7280);
}
.preview-title {
  font-weight: 600;
  margin-top: 2px;
}
.preview-description {
  font-size: 0.85rem;
  color: var(--label-secondary, #6b7280);
  margin-top: 2px;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
}
.image-card {
  border: 1px solid var(--surface-border);
  border-radius: var(--radius-md, 8px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.image-card.selected {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 1px var(--primary-color);
}
.image-card img {
  width: 100%;
  aspect-ratio: 1200 / 630;
  object-fit: cover;
  display: block;
  background: var(--surface-100);
}
.image-info {
  padding: 0.5rem 0.75rem;
  flex: 1;
}
.image-name {
  font-size: 0.85rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.image-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0.5rem 0.5rem;
}

@media (max-width: 900px) {
  .link-grid {
    grid-template-columns: 1fr;
  }
}
</style>
